import { Button, Container, Typography } from '@pwskills/rachnaui';
import React, { useContext, useState } from 'react';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
import { classNames } from '@pwskills/rachnaui/utils';
import { useRouter } from 'next/router';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import { GAevent } from '@/utils/firebase.utils';
import useUserDetails from '@/store/userDetails';
import { sendSkCtaClickedEvent, sendSkFormStartedEvent } from '@/utils/GAEventFunction';
import LeadCapture from '../LeadCapture';
import { LeadCaptureType, ModalType } from '../../../interfaces/leadCapture.enum';

type Props = {
  isConfused: boolean;
  title: string;
  subTitle: string;
  courseId?: string;
  modalType?: string;
  buttonText: string;
  img: string;
  msgLineOne: string;
  msgLineTwo: string;
  type: string;
  enquiryType: string;
  enquiryTypeNPF: string;
  pageName: string;
};

const GetConnected = ({
  isConfused = true,
  title,
  subTitle,
  courseId,
  modalType,
  buttonText,
  img,
  msgLineOne,
  msgLineTwo,
  type,
  enquiryType,
  enquiryTypeNPF,
  pageName,
}: Props) => {
  const [openModal, setOpenModal] = useState(false);
  const { courseDetails } = useContext(CourseDescriptionContext);
  const router = useRouter();
  const { userDetails: user, userIsLoggedIn: isLoggedIn, isUserPaid } = useUserDetails();
  const isDashboard = router.pathname.includes('/dashboard');

  const utmQuery = useUTMParameters();
  const sendGAEvent = (eventName: string) => {
    const GAEventData = {
      click_cta: 'Get Connected Now',
      page_ref: router?.query?.from || '',
      utm_source: utmQuery?.utm_source || '',
      utm_medium: utmQuery?.utm_medium || '',
      utm_campaign: utmQuery?.utm_campaign || '',
    };
    GAevent(eventName, GAEventData);
  };

  const returnChildren = () => {
    return (
      <div className="flex flex-col medium:!flex-row justify-center medium:!justify-between items-center gap-y-4 w-full">
        <div className="space-y-6 flex flex-col justify-center items-center medium:!items-start medium:w-2/3">
          <header className="relative">
            <Typography
              component="h3"
              variant="bold"
              className="text-gray-850 text-center medium:!text-left"
            >
              {title}
            </Typography>
            <Typography
              component="body-regular"
              variant="regular"
              className="text-gray-650 text-center medium:!text-left"
            >
              {subTitle}{' '}
              {isConfused ? (
                <Typography
                  component="body-regular"
                  variant="bold"
                  className="inline-block text-green-700"
                >
                  Achieve your dreams!
                </Typography>
              ) : (
                <Typography
                  component="body-regular"
                  variant="bold"
                  className="inline-block text-gray-850"
                >
                  24 hour
                </Typography>
              )}
            </Typography>
            {!isConfused && (
              <figure className="hidden medium:!block absolute right-0">
                <img src="/images/courseDescription/arrow.svg" alt="logo" />
              </figure>
            )}
          </header>

          <Button
            className={classNames(
              ' mx-auto medium:!ml-0 pt-2 px-4',
              isConfused ? 'bg-green-800 hover:bg-green-800' : 'bg-blue-700 hover:bg-blue-700'
            )}
            size="small"
            onClick={() => {
              sendSkCtaClickedEvent({
                userData: user,
                isLoggedIn,
                isUserPaid,
                page_path: router?.pathname,
                click_text: 'Get Connected Now',
                click_id: 'get_connected_now',
                click_position: isConfused ? 'still_confused_strip' : 'counsellor_strip',
                widget_name: isConfused ? 'still_confused_strip' : 'counsellor_strip',
                page_referrer_cta: (router?.query?.clickText as string) || '',
                page_referrer_widget_name: (router?.query?.section as string) || '',
                course_name: courseDetails?.name || courseDetails?.title || '',
                course_slug: courseDetails?.slug || '',
                course_category: courseDetails?.parentCategoryDetails?.title || '',
                course_id: courseId || courseDetails?._id || '',
                price: Number(courseDetails?.pricing?.IN || 0),
                discount_percentage: Number(courseDetails?.pricing?.discount || 0),
                is_free: !!courseDetails?.pricing?.isFree,
              });

              setOpenModal(true);
              sendGAEvent('sk_form_start');
              sendSkFormStartedEvent({
                userData: user,
                isLoggedIn,
                isUserPaid,
                page_path: router?.pathname,
                page_referrer_name: pageName,
                page_referrer_cta: 'Get Connected Now',
                page_referrer_widget_name: isConfused ? 'still_confused_strip' : 'counsellor_strip',
                form_id: 'lead_form_get_connected',
                form_name: 'Get Connected Lead Form',
                form_type: 'lead',
                form_location: pageName || 'course_description_page',
                form_time_fill_sec: 0,
                course_batch_name: courseDetails?.name || courseDetails?.title || '',
                course_batch_slug: courseDetails?.slug || '',
                course_batch_category: courseDetails?.parentCategoryDetails?.title || '',
                course_batch_id: courseId || courseDetails?._id || '',
                price: Number(courseDetails?.pricing?.IN || 0),
                discount_percentage: Number(courseDetails?.pricing?.discount || 0),
                is_free: !!courseDetails?.pricing?.isFree,
              });
            }}
          >
            Get Connected Now
          </Button>
        </div>
        <figure className="hidden regular:!flex absolute top-0 right-1/3">
          <img src="/images/courseDescription/yellow-semicircle-top.svg" alt="illustration" />
        </figure>
        <figure className="hidden medium:!flex !absolute bottom-0 large:!-translate-x-32 w-[238px] medium:!shrink-0 medium:!right-0">
          {isConfused ? (
            <img src="/images/courseDescription/confused-person.svg" alt="confused-person" />
          ) : (
            <img src="/images/courseDescription/counsellor.webp" alt="counsellor" />
          )}
        </figure>
      </div>
    );
  };

  return (
    <section
      id={isConfused ? 'get-connected' : 'blue-get-connected'}
      className={classNames(
        isDashboard ? 'relative p-4' : 'py-8 medium:!py-16 large:!py-[89px] relative',
        isConfused ? 'still-confused' : 'counsellor'
      )}
    >
      {isDashboard ? returnChildren() : <Container>{returnChildren()}</Container>}
      <figure className="w-16 regular:!w-auto absolute right-0 top-0">
        {isConfused ? (
          <img src="/images/courseDescription/green-semi-circle.svg" alt="bg" />
        ) : (
          <img src="/images/courseDescription/blue-semi-circle.png" alt="bg" />
        )}
      </figure>
      <figure className="absolute left-0 bottom-0">
        <img src="/images/courseDescription/yellow-svg.svg" alt="bg-illus" />
      </figure>
      {openModal && (
        <LeadCapture
          leadCaptureType={LeadCaptureType.MODAL}
          {...(!(modalType === ModalType.ONLINE_DEGREE) && {
            courseId: courseId || courseDetails?._id || '',
          })}
          modelOpen={openModal}
          setModelOpen={setOpenModal}
          buttonText={buttonText}
          img={img}
          msgLineOne={msgLineOne}
          msgLineTwo={msgLineTwo}
          type={type}
          enquiryType={enquiryType}
          enquiryTypeNPF={enquiryTypeNPF}
          {...(modalType && { modalType })}
          pageName={pageName}
          sourceName="course_description_get_connected"
        />
      )}
    </section>
  );
};

export default GetConnected;
